import type { CommentMention } from "./types";

/**
 * The `@`-mention token format for comment bodies, and the parser that reads it.
 *
 * A body carries its mentions inline as `@[Ann Lee](user:4821)`, so the text is
 * self-describing: the id identifies the target and the name is only what the
 * author saw when they wrote it. Two things follow, and both are the reason for
 * the format.
 *
 * The name is display cache, never identity. A rename leaves historical bodies
 * saying "Ann Lee" while the row still points at 4821, so renderers resolve the
 * current name from the id and treat the stored one as a fallback for a target
 * they can't look up. Matching on the name instead — searching the body for
 * "@Ann Lee" — silently stops finding a mention the moment someone changes
 * their name, and can't tell two colleagues of the same name apart.
 *
 * And because the ids are in the text, the mention rows are derivable from the
 * body rather than asserted alongside it. `asset_comment_mentions` stays the
 * queryable index — "which comments mention me" is not a table scan over
 * bodies — but it is a projection of the body, and the service checks the two
 * agree before writing either.
 *
 * This module is shared with the frontend composer and renderer: the token
 * format has exactly one definition, and both sides read it from here.
 */

const USER_TARGET_PREFIX = "user:";
const REVIEW_AGENT_TARGET_PREFIX = "agent:";
/** Hightouch's agent has no id, so its target is the whole token. */
const AGENT_TARGET = "hightouch-agent";

/**
 * `@[Display Name](user:4821)` or `@[Brand reviewer](agent:<uuid>)`.
 *
 * The name run excludes brackets so it can't swallow a following token, and the
 * target excludes parens and whitespace for the same reason. Both are
 * non-greedy in effect rather than by backtracking, which keeps this linear
 * over the body.
 *
 * Exported for renderers that need each occurrence's position, which
 * {@link parseMentionTokens} discards when it dedupes. It carries the `g` flag,
 * so callers must not share `lastIndex` across scans — use `matchAll`, which
 * works on a copy.
 */
export const MENTION_TOKEN_PATTERN = /@\[([^\][]+)\]\(([^()\s]+)\)/g;

/** A mention as it appears in a body, with the name the author saw. */
export type ParsedCommentMention = {
  mention: CommentMention;
  /** The display name at the time of writing. Never used as identity. */
  label: string;
};

/**
 * Null when the target names nothing we can resolve — an unknown kind, a
 * malformed id.
 *
 * Exported because a renderer walking {@link MENTION_TOKEN_PATTERN} directly has
 * to make the same call: the pattern matches the token's shape, and this is what
 * decides whether the shape is a mention or prose that happens to look like one.
 */
export function parseTarget(target: string): CommentMention | null {
  if (target === AGENT_TARGET) {
    return { kind: "agent" };
  }

  if (target.startsWith(USER_TARGET_PREFIX)) {
    const raw = target.slice(USER_TARGET_PREFIX.length);

    // Bare digits only: `Number` would otherwise accept "0x12", "1e3" and
    // " 12", none of which are ids we wrote.
    if (!/^\d+$/.test(raw)) {
      return null;
    }

    const userId = Number(raw);

    // Beyond this a bigint id no longer round-trips through a JS number, and
    // the value we'd write back is not the one in the body.
    if (!Number.isSafeInteger(userId)) {
      return null;
    }

    return { kind: "user", userId };
  }

  if (target.startsWith(REVIEW_AGENT_TARGET_PREFIX)) {
    const reviewAgentId = target.slice(REVIEW_AGENT_TARGET_PREFIX.length);

    if (reviewAgentId.length === 0) {
      return null;
    }

    return { kind: "reviewAgent", reviewAgentId };
  }

  return null;
}

/** The key a target is deduped and compared on. */
export function mentionKey(mention: CommentMention): string {
  switch (mention.kind) {
    case "user":
      return `${USER_TARGET_PREFIX}${mention.userId}`;
    case "reviewAgent":
      return `${REVIEW_AGENT_TARGET_PREFIX}${mention.reviewAgentId}`;
    case "agent":
      return AGENT_TARGET;
  }
}

/**
 * Renders a mention back into body text.
 *
 * Brackets in the label become parens rather than being escaped or dropped: the
 * name run in {@link MENTION_TOKEN_PATTERN} excludes brackets so a token can't
 * swallow the one after it, so a name containing one would write a token
 * nothing can parse — and the service, which checks the mention rows against
 * the body, would then reject the comment outright. Substituting rather than
 * deleting keeps a label that is *only* brackets from becoming empty, which the
 * pattern would also refuse to match. The label is display cache and the id is
 * what identifies the target, so the substitution costs nothing.
 */
export function formatMentionToken(
  mention: CommentMention,
  label: string,
): string {
  const safeLabel = label.replaceAll("[", "(").replaceAll("]", ")");

  return `@[${safeLabel}](${mentionKey(mention)})`;
}

/**
 * Every mention token in a body, in the order it appears, deduplicated by
 * target.
 *
 * Naming someone twice in one comment is ordinary prose and means one
 * notification, matching the partial unique indexes on the mention table. The
 * first occurrence wins, so the retained label is the one the reader meets
 * first.
 *
 * Anything that isn't a well-formed token — a bare `@name`, an unknown target
 * kind, a malformed id — is left as prose rather than rejected. A body is text
 * first; `@` is a character people type.
 */
export function parseMentionTokens(body: string): ParsedCommentMention[] {
  const byTarget = new Map<string, ParsedCommentMention>();

  for (const match of body.matchAll(MENTION_TOKEN_PATTERN)) {
    const [, label, target] = match;

    // Both groups are non-optional in the pattern, so a match always has them;
    // narrowed rather than asserted so this compiles under the app's stricter
    // indexed-access checking too.
    if (label === undefined || target === undefined) {
      continue;
    }

    const mention = parseTarget(target);

    if (mention === null) {
      continue;
    }

    const key = mentionKey(mention);

    if (!byTarget.has(key)) {
      byTarget.set(key, { mention, label });
    }
  }

  return [...byTarget.values()];
}

/** The mention targets a body names, as the service stores them. */
export function mentionsFromBody(body: string): CommentMention[] {
  return parseMentionTokens(body).map((parsed) => parsed.mention);
}

/** Hightouch's agent has no mention row, so its token in the body is the only record. */
export function mentionsAgent(body: string): boolean {
  return mentionsFromBody(body).some((mention) => mention.kind === "agent");
}

/**
 * Current display names for mention targets, keyed by {@link mentionKey}.
 *
 * Passing the names in rather than reading the ones stored in the body is the
 * point: the token preserves the name as of authoring, so a target that has
 * since been renamed renders under the name they go by now.
 */
export type MentionNames = ReadonlyMap<string, string>;

/** A run of a comment body, once the mentions have been picked out of it. */
export type BodySegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; mention: CommentMention; label: string };

/**
 * Splits a body into plain and mention runs, resolving each mention's current
 * name.
 *
 * Shared by everything that renders a body — the app's comment surfaces and
 * feed, and the mention email — because they must agree on what counts as a
 * mention. They didn't, when they walked the tokens separately: the email
 * styled `@[note](nope)` as a mention while the app left it as the prose it is.
 *
 * Driven by the tokens in the text, not by a list of names searched for inside
 * it. Matching on names missed a mention the moment someone was renamed, and
 * couldn't tell two people of the same name apart — the id in the token settles
 * both.
 *
 * Every occurrence, not every distinct target: naming someone twice is one
 * mention row but two spans to render.
 *
 * A token whose target isn't in `names` still comes back as a mention, falling
 * back to the name the author saw. That covers a member who has since left the
 * workspace: the mention was real, so it should still read as one. A
 * token-shaped run whose target doesn't parse at all is prose, and stays in the
 * plain run around it.
 */
export function splitBodyOnMentions(
  body: string,
  names: MentionNames,
): BodySegment[] {
  const segments: BodySegment[] = [];
  let plainFrom = 0;

  for (const match of body.matchAll(MENTION_TOKEN_PATTERN)) {
    const [raw, label, target] = match;
    const at = match.index;

    if (label === undefined || target === undefined) {
      continue;
    }

    // The pattern matches a token's shape; `parseTarget` is what decides the
    // shape is a mention.
    const mention = parseTarget(target);

    if (mention === null) {
      continue;
    }

    if (at > plainFrom) {
      segments.push({ kind: "text", text: body.slice(plainFrom, at) });
    }

    segments.push({
      kind: "mention",
      mention,
      label: names.get(mentionKey(mention)) ?? label,
    });
    plainFrom = at + raw.length;
  }

  if (plainFrom < body.length) {
    segments.push({ kind: "text", text: body.slice(plainFrom) });
  }

  return segments;
}

/**
 * A body as plain text, each mention token rewritten to `@Name`.
 *
 * For readers that consume the body as prose rather than rendering it, such as
 * an agent reading a thread. The names come from `names` per
 * {@link splitBodyOnMentions}, so a renamed colleague reads under their current
 * name and one who has left keeps the name the author saw.
 */
export function renderBodyWithMentionNames(
  body: string,
  names: MentionNames,
): string {
  return splitBodyOnMentions(body, names)
    .map((segment) =>
      segment.kind === "text" ? segment.text : `@${segment.label}`,
    )
    .join("");
}
